import type {
  ConnectedGitHubRepositoryDTO,
  IntegrationType,
  ProviderCheckSummaryDTO,
} from '@deepguide-ai/types'
import { formatTimestamp } from '@/lib/format'

export const INTEGRATION_DETAIL_TABS = [
  'overview',
  'tests',
  'services',
  'health',
  'scope',
  'routing',
] as const

export type IntegrationDetailTab = (typeof INTEGRATION_DETAIL_TABS)[number]

export type IntegrationCatalogProvider = {
  id: IntegrationType
  name: string
  description: string
  category: string
  available: boolean
  authMethod?: 'oauth' | 'credentials'
  parentType?: IntegrationType
  securityProfileSlug?: string
  state?: 'live' | 'connect-only' | 'coming-soon' | 'attest-only'
  logoPath?: string
  checkCount?: number
  scanProvider?: string
  connectionProviderKey?: string
}

export function findCatalogProvider(
  providers: IntegrationCatalogProvider[] | undefined,
  providerSlug: string
): IntegrationCatalogProvider | null {
  const normalized = providerSlug.toLowerCase()
  return (
    providers?.find(
      (provider) =>
        provider.securityProfileSlug?.toLowerCase() === normalized ||
        provider.id.toLowerCase() === normalized
    ) ?? null
  )
}

export function summarizeChecks(checks: ProviderCheckSummaryDTO[]) {
  let passed = 0
  let failed = 0
  let review = 0
  let notRun = 0

  for (const check of checks) {
    if (check.lastResult === 'pass') passed += 1
    else if (check.lastResult === 'fail') failed += 1
    else if (
      check.lastResult === 'error' ||
      check.lastResult === 'warn' ||
      check.lastResult === 'interrupted'
    ) {
      review += 1
    } else notRun += 1
  }

  return { total: checks.length, passed, failed, review, notRun }
}

export function matchesCheckResultFilter(
  result: ProviderCheckSummaryDTO['lastResult'],
  filter: string
) {
  if (filter === 'all') return true
  if (filter === 'review') return result === 'error' || result === 'warn' || result === 'interrupted'
  if (filter === 'not_run') {
    return !['pass', 'fail', 'warn', 'error', 'interrupted'].includes(result ?? '')
  }
  return result === filter
}

export function getChecksForService(
  checks: ProviderCheckSummaryDTO[],
  serviceId: string
) {
  return checks.filter((check) => check.resourceServiceId === serviceId)
}

/** Applies the backend's service scope to auditor checks. */
export function excludeNotUsedServiceChecks(
  checks: ProviderCheckSummaryDTO[]
) {
  return checks.filter((check) => !check.disabledByServiceScope)
}

export function supportsTicketRouting(type: string) {
  return type === 'GitHub' || type === 'Jira' || type === 'Linear'
}

export function supportsCommunicationRouting(type: string) {
  return type === 'Slack' || type === 'MicrosoftTeams'
}

const MULTI_ACCOUNT_PROVIDER_IDS = new Set([
  'AWS_READONLY',
  'GCP_READONLY',
  'AZURE_READONLY',
  'Cloudflare',
  'Aliyun',
  'TencentCloud',
  'GitHub',
])

export function supportsMultipleAccounts(provider: IntegrationCatalogProvider) {
  return MULTI_ACCOUNT_PROVIDER_IDS.has(provider.id)
}

export function getIntegrationsLink(
  workspaceId: string,
  auditCycleId?: string
) {
  return auditCycleId
    ? {
        to: '/workspaces/$workspaceId/auditor-center/$auditCycleId/integrations' as const,
        params: { workspaceId, auditCycleId },
      }
    : {
        to: '/workspaces/$workspaceId/integrations' as const,
        params: { workspaceId },
      }
}

export function getIntegrationProviderLink(
  workspaceId: string,
  providerSlug: string,
  auditCycleId?: string
) {
  return auditCycleId
    ? {
        to: '/workspaces/$workspaceId/auditor-center/$auditCycleId/integrations/$providerSlug' as const,
        params: { workspaceId, auditCycleId, providerSlug },
      }
    : {
        to: '/workspaces/$workspaceId/integrations/$providerSlug' as const,
        params: { workspaceId, providerSlug },
      }
}

export function getIntegrationServiceLink(
  workspaceId: string,
  providerSlug: string,
  serviceId: string,
  auditCycleId?: string
) {
  return auditCycleId
    ? {
        to: '/workspaces/$workspaceId/auditor-center/$auditCycleId/integrations/$providerSlug/services/$serviceId' as const,
        params: { workspaceId, auditCycleId, providerSlug, serviceId },
      }
    : {
        to: '/workspaces/$workspaceId/integrations/$providerSlug/services/$serviceId' as const,
        params: { workspaceId, providerSlug, serviceId },
      }
}

export function getIntegrationAccountLink(
  workspaceId: string,
  providerSlug: string,
  connectionId: string,
  auditCycleId?: string
) {
  return auditCycleId
    ? {
        to: '/workspaces/$workspaceId/auditor-center/$auditCycleId/integrations/$providerSlug/accounts/$connectionId' as const,
        params: { workspaceId, auditCycleId, providerSlug, connectionId },
      }
    : {
        to: '/workspaces/$workspaceId/integrations/$providerSlug/accounts/$connectionId' as const,
        params: { workspaceId, providerSlug, connectionId },
      }
}

export function getIntegrationDetailLink(
  provider: IntegrationCatalogProvider,
  workspaceId: string,
  providerSlug: string,
  connectionId: string,
  tab?: IntegrationDetailTab,
  auditCycleId?: string
) {
  if (supportsMultipleAccounts(provider)) {
    return {
      ...getIntegrationAccountLink(
        workspaceId,
        providerSlug,
        connectionId,
        auditCycleId
      ),
      search: { tab },
    }
  }
  return {
    ...getIntegrationProviderLink(workspaceId, providerSlug, auditCycleId),
    search: { tab, connection: connectionId },
  }
}

export function canShowScope(
  provider: IntegrationCatalogProvider | null,
  scanProvider: string,
  accountConnectionId?: string
) {
  return Boolean(
    scanProvider &&
      provider &&
      (!supportsMultipleAccounts(provider) || accountConnectionId)
  )
}

export function getGithubRepositoryConnections(
  repositories: Array<Pick<ConnectedGitHubRepositoryDTO, 'id' | 'installationId' | 'fullName'>>
) {
  return repositories.flatMap((repository) => repository.installationId ? [{
    connectionId: repository.installationId,
    repositoryId: repository.id,
    label: repository.fullName,
  }] : [])
}

export function formatIntegrationTime(value?: string | Date | null) {
  if (!value) return 'Never'
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return 'Unavailable'
  return formatTimestamp(date)
}

export function isInteractiveTileTarget(
  target: EventTarget | null,
  card: EventTarget | null
) {
  if (!(target instanceof Element)) return false
  const interactive = target.closest(
    'button, a, input, select, textarea, [role="button"], [role="menuitem"], [data-prevent-detail-navigation]'
  )
  return Boolean(interactive && interactive !== card)
}
